import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import AppLayout from "../../../default/components/AppLayout";
import StyledButton from "../../../default/components/StyledButton";
import { claimTestTicket } from "../../../utils/api";
import { lineAccountLinks } from "../../../constants/links";

const SOUL_ID = "nazuna_yofukashi_line_ja";
const LINE_URL = lineAccountLinks.yofukashi.ja.nazuna;

export default function TestTicket() {
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(
    () => emailAddress.trim() && password.trim() && !isSubmitting,
    [emailAddress, password, isSubmitting]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setError("");
    setIsSubmitting(true);
    try {
      await claimTestTicket(SOUL_ID, {
        emailAddress: emailAddress.trim(),
        password,
      });
      window.location.assign(LINE_URL);
    } catch (err) {
      setError(err.message || "テスト券の付与に失敗しました。");
      setIsSubmitting(false);
    }
  };

  const textFieldSx = (theme) => ({
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.78)",
      fontFamily: theme.system.fonts.body,
      fontWeight: 700,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#FFFFFF",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      borderRadius: "8px",
      color: "#FFFFFF",
      fontFamily: theme.system.fonts.body,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 255, 255, 0.72)",
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#FFFFFF",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#FFFFFF",
      },
    },
    "& .MuiInputBase-input": {
      color: "#FFFFFF",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(255, 255, 255, 0.68)",
      opacity: 1,
    },
  });

  return (
    <AppLayout page="mypage" width="92%">
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={(theme) => ({
            width: "100%",
            maxWidth: 420,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2.5,
            p: 3,
            borderRadius: 2,
            backgroundColor: theme.system.colors.background.card,
            boxShadow: "0 8px 28px rgba(0, 0, 0, 0.22)",
          })}
        >
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography
              variant="h1"
              sx={(theme) => ({
                mb: 1,
                fontSize: "26px",
                lineHeight: 1.45,
                fontFamily: theme.system.fonts.heading,
              })}
            >
              関係者様専用チケット
              <br />
              七草ナズナ
            </Typography>
            <Typography variant="body2">
              メールアドレスとパスワードを入力してください。
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            required
            variant="outlined"
            type="email"
            label="メールアドレス"
            placeholder="example@example.com"
            value={emailAddress}
            autoComplete="email"
            onChange={(event) => setEmailAddress(event.target.value)}
            sx={textFieldSx}
          />

          <TextField
            fullWidth
            required
            variant="outlined"
            type="password"
            label="パスワード"
            placeholder="パスワードを入力"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
            sx={textFieldSx}
          />

          <StyledButton
            type="submit"
            disabled={!canSubmit}
            sx={(theme) => ({
              mt: 1,
              width: "100%",
              backgroundColor: canSubmit
                ? "rgba(255, 255, 255, 0.16)"
                : "rgba(255, 255, 255, 0.08)",
              color: "#FFFFFF",
              borderColor: canSubmit
                ? "#FFFFFF"
                : "rgba(255, 255, 255, 0.62)",
              fontFamily: theme.system.fonts.heading,
              fontSize: "16px",
              opacity: 1,
              "&.Mui-disabled": {
                color: "rgba(255, 255, 255, 0.78)",
                borderColor: "rgba(255, 255, 255, 0.62)",
              },
            })}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : !canSubmit ? (
              "メールとパスワードを入力"
            ) : (
              "チケットを受け取る"
            )}
          </StyledButton>
        </Box>
      </Box>
    </AppLayout>
  );
}
